<template>
    <PopoverRoot v-model:open="isPopoverOpen">
        <PopoverTrigger as-child>
            <button type="button" class="favorites-trigger" @click="onTriggerClick">
                <Icon class="w-5 h-5" name="watchlist" />
                <!-- <span>Favorites</span> -->
                <span v-if="totalFavorites > 0" class="favorites-count">{{ totalFavorites }}</span>
            </button>
        </PopoverTrigger>

        <PopoverPortal>
            <PopoverContent class="favorites-panel" align="end" :side-offset="8" @click.stop>
                <div class="panel-header">
                    <div class="panel-title">
                        <h4>Your Favorite Products</h4>
                    </div>
                    <!-- <router-link to="/favorites" class="view-all" @click="closePopover">View all favorites</router-link> -->
                    <button type="button" class="panel-close" @click="fetchFavorites">
                        <RotateCcw class="w-4 h-4" />
                    </button>
                </div>

                <div class="favorites-list" v-if="loading">
                    <div class="favorite-item" v-for="x in 4" :key="x">
                        <div class="thumb skeleton"></div>
                        <div class="content">
                            <div class="line skeleton"></div>
                            <div class="line short skeleton"></div>
                            <div class="line price skeleton"></div>
                        </div>
                        <div class="action-line skeleton"></div>
                    </div>
                </div>

                <div class="empty" v-else-if="!items.length">
                    No favorites yet.
                </div>

                <div class="favorites-list" v-else>
                    <div v-for="item in items" :key="item.id" class="favorite-item">
                        <router-link :to="productRoute(item)" class="item-main" @click="closePopover">
                            <img class="thumb" :src="getImage(item)" :alt="item.name" />
                            <div class="content">
                                <p>{{ item.name }}</p>
                                <span>{{ item.brand }} - {{ item.product_type }}</span>
                                <strong class="price">${{ getDisplayPrice(item) }}</strong>
                            </div>
                        </router-link>

                        <div class="item-actions">
                            <button type="button" class="text-theme-red p-1" @click="toggleFavorite(item)">
                                <Icon name="heart-outline" class="w-4 h-4 text-theme-red" />
                            </button>

                            <div class="qty-wrap" v-if="canAddToCart(item)">
                                <PlusMinusInput class="medium" :id="getDefaultVariant(item).id"
                                    v-model:modelValue="qtyMap[getDefaultVariant(item).id]" :min="0"
                                    :max="getVariantMax(item)" :hideDelete="true"
                                    @getQuantity="onQtyChange(item, $event)" />
                            </div>
                            <template v-else>
                                <button v-if="getDefaultVariant(item)?.has_notification"
                                    @click="doNotNotify(item)" class="notify-bell" :class="{ undo: true }"
                                    v-tippy="{ content: 'You will be notified when this product is available.', placement: 'bottom-end' }">
                                    <div class="n-icon bg-black">
                                        <Icon name="bell-off" class="w-4 h-4" />
                                    </div>
                                </button>
                                <button v-else @click="notifyMe(item)" class="notify-bell"
                                    v-tippy="{ content: 'Click here to be notified when this product is available.', placement: 'bottom-end' }">
                                    <div class="n-icon text-brand-gray">
                                        <Icon name="bell-ring" />
                                    </div>
                                </button>
                            </template>
                        </div>
                    </div>
                </div>

            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
</template>

<script>
import { PopoverRoot, PopoverTrigger, PopoverPortal, PopoverContent } from "radix-vue";
import { inject } from "vue";
import { useStore } from "vuex";
import Watchlist from "@/modules/Watchlist";
import Notifications from "@/modules/Notifications";
import PlusMinusInput from "@/components/PlusMinusInput.vue";
import { RotateCcw } from "lucide-vue-next";

export default {
    name: "FavoritesPopover",
    components: {
        PopoverRoot,
        PopoverTrigger,
        PopoverPortal,
        PopoverContent,
        PlusMinusInput,
        RotateCcw
    },
    data() {
        return {
            isPopoverOpen: false,
            loading: false,
            items: [],
            totalFavorites: 0,
            qtyMap: {},
        };
    },
    mounted() {
        this.fetchFavorites();
    },
    methods: {
        onTriggerClick() {
            // Keep cart behavior aligned with existing navbar overlays.
            this.$emit("interact");
        },
        closePopover() {
            this.isPopoverOpen = false;
        },
        async fetchFavorites() {
            if (!this.axios?.isLoggedIn) return;
            this.loading = true;
            try {
                const resp = await this.axios.authApi.get("/watchlist", {
                    params: {
                        page: 1,
                        offset: 6,
                    },
                });

                const payload = resp?.data?.data || {};
                this.items = payload?.data || [];
                this.totalFavorites = payload?.total || this.items.length;
                this.syncQtyMap();
            } catch (error) {
                this.items = [];
                this.totalFavorites = 0;
            } finally {
                this.loading = false;
            }
        },
        productRoute(item) {
            if (item?.is_bundle == "true" || item?.is_bundle === true) {
                return `/bundles/${item.slug}`;
            }
            return `/products/${item.slug}`;
        },
        getImage(item) {
            if (!item?.image_url) return "/images/medusa.png";
            return item.image_url;
        },
        getDefaultVariant(item) {
            if (!item?.variants?.length) return null;
            return item.variants.find((v) => v?.id) || item.variants[0] || null;
        },
        canAddToCart(item) {
            const variant = this.getDefaultVariant(item);
            if (!variant) return false;
            return (
                Number(variant.available_stock) > 0 &&
                variant.banned !== "true" &&
                variant.price != null &&
                Number(variant.price) !== 0
            );
        },
        getVariantMax(item) {
            const variant = this.getDefaultVariant(item);
            if (!variant) return 0;
            const stock = Number(variant.available_stock) || 0;
            const maxOrder = Number(variant.MaxOrderQty) || 0;
            if (!maxOrder) return stock;
            return maxOrder > stock ? stock : maxOrder;
        },
        getDisplayPrice(item) {
            const variant = this.getDefaultVariant(item);
            if (!variant) return "0.00";
            const value =
                variant?.wholesale_price ??
                variant?.price ??
                item?.price ??
                0;
            return Number(value).toFixed(2);
        },
        getCurrentQty(variantId) {
            let qty = 0;
            const cart = this.store?.getters?.cart;
            cart?.itemsByCat?.forEach((category) => {
                category?.items?.forEach((cartItem) => {
                    if (cartItem.variantId == variantId) {
                        qty = Number(cartItem.qty) || 0;
                    }
                });
            });
            return qty;
        },
        syncQtyMap() {
            const nextMap = {};
            this.items.forEach((item) => {
                const variant = this.getDefaultVariant(item);
                if (variant?.id) {
                    nextMap[variant.id] = this.getCurrentQty(variant.id);
                }
            });
            this.qtyMap = nextMap;
        },
        async onQtyChange(item, payload) {
            const variant = this.getDefaultVariant(item);
            if (!variant) return;

            const [qty, add] = payload;
            const currentQty = this.getCurrentQty(variant.id);

            try {
                if (qty === 0) {
                    await this.store.dispatch("removeFromCart", {
                        variant_id: variant.id,
                        qty: currentQty,
                        segmentQty: currentQty,
                        axios: this.axios,
                        route: this.$route,
                    });
                } else {
                    await this.store.dispatch("UpdateCart", {
                        variant_id: variant.id,
                        qty,
                        segmentQty: add ? qty - currentQty : currentQty - qty,
                        axios: this.axios,
                        route: this.$route,
                        add,
                    });
                }
            } catch (e) {
                this.qtyMap[variant.id] = this.getCurrentQty(variant.id);
            }
        },
        notifyMe(item) {
            const variant = this.getDefaultVariant(item);
            if (!variant) return;
            this.notifyme(variant, item);
            variant.has_notification = true;
        },
        doNotNotify(item) {
            const variant = this.getDefaultVariant(item);
            if (!variant) return;
            this.deleteNotify(variant, item);
            variant.has_notification = null;
        },
        async toggleFavorite(item) {
            await this.setWatchlist(item);
            item.watchlist = item.watchlist ? null : 1;
            if (!item.watchlist) {
                this.items = this.items.filter((fav) => fav.id !== item.id);
                this.totalFavorites = Math.max(0, this.totalFavorites - 1);
                this.syncQtyMap();
            }
        },
    },
    setup() {
        const axios = inject("axios");
        const store = useStore();
        const { setWatchlist } = Watchlist();
        const { notifyme, deleteNotify } = Notifications();
        return { axios, store, setWatchlist, notifyme, deleteNotify };
    },
};
</script>

<style lang="scss">
.favorites-dropdown {
    @apply relative;
}

.favorites-trigger {
    @apply flex items-center space-x-1.5 text-sm text-brand-body mr-4 hover:text-brand-secondary bg-transparent border-none p-0;
}

.favorites-count {
    @apply inline-flex items-center justify-center rounded-full bg-theme-secondary text-white text-xxs;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
}

.favorites-panel {
    @apply origin-top-right absolute top-full right-0 mt-2 shadow-nav bg-white ring-1 ring-black ring-opacity-10 rounded-lg focus:outline-none z-50;
    width: 34rem;
    max-width: calc(100vw - 1.5rem);
}

.panel-header {
    @apply flex items-center justify-between px-4 py-3 border-b border-gray-100;

    .panel-title {
        @apply flex items-center;
    }

    h4 {
        @apply text-sm font-semibold text-theme-secondary;
    }

    .panel-close {
        @apply text-theme-body hover:text-theme-secondary;
    }
}

.favorites-list {
    @apply overflow-y-auto p-2;
    max-height: 24rem;
    @include CssScrollbar();
}

.favorite-item {
    @apply flex items-center justify-between gap-2 px-2 py-2.5 border-b border-gray-100 bg-white;

    &:last-child {
        border-bottom: none;
    }

    .item-main {
        @apply flex items-center gap-3 min-w-0 flex-grow;
    }

    .thumb {
        @apply w-12 h-12 rounded border border-gray-100 object-contain bg-white flex-shrink-0;
    }

    .content {
        @apply min-w-0 flex-grow;

        p {
            @apply text-xs font-semibold text-theme-secondary truncate leading-tight;
        }

        span {
            @apply text-xxs font-light text-theme-body truncate block;
        }

        .price {
            @apply text-xxs font-medium text-theme-secondary block;
            line-height: 1.1;
            margin-top: 2px;
        }

        .line {
            @apply h-3 rounded bg-gray-100;
            width: 130px;

            &.short {
                width: 90px;
                margin-top: 6px;
            }

            &.price {
                width: 65px;
                margin-top: 6px;
            }
        }
    }

    .item-actions {
        @apply flex items-center gap-3;
    }



    .qty-wrap {
        min-width: 96px;
    }

    .add-unavailable {
        @apply text-xs text-theme-body;
        opacity: 0.7;
        min-width: 28px;
        text-align: center;
    }

    .action-line {
        @apply h-8 rounded bg-gray-100;
        width: 90px;
    }
}

.notify-bell {
    @apply flex items-center space-x-2 uppercase text-xs tracking-tight;

    .n-icon {
        @apply inline-flex items-center justify-center w-8 h-8 text-white rounded;
        background: #e5e7eb;

        svg {
            @apply w-4 h-4;
        }
    }

    &.undo .n-icon {
        @apply bg-black;
    }
}

.empty {
    @apply px-4 py-6 text-sm text-theme-body text-center;
}

.view-all {
    @apply text-xs font-medium text-theme-secondary hover:underline;
}


.skeleton {
    animation: pulse 1.4s ease-in-out infinite;
}

@keyframes pulse {
    0% {
        opacity: 0.55;
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0.55;
    }
}
</style>
